import { Navigate, useLocation } from "react-router-dom";
import AdminPage from "../../../components/cares/admin/admin-page";
import AdminSettingsPage from "../../../components/cares/admin-settings/admin-settings-page";
import ClientWorkspacePage from "../../../components/cares/client-workspace/client-workspace-page";
import CalendarPage from "../../../components/cares/my-schedule/calendar-page";
import GroupSchedulePage from "../../../components/cares/my-schedule/group-schedule-page";
import GroupTalliesPage from "../../../components/cares/my-schedule/group-tallies-page";
import MyScheduleHomePage from "../../../components/cares/my-schedule/my-schedule-home-page";
import RequestPolicyPage from "../../../components/cares/my-schedule/request-policy-page";
import RequestsPage from "../../../components/cares/my-schedule/requests-page";
import SwapShiftsPage from "../../../components/cares/my-schedule/swap-shifts-page";
import MyProfilePage from "../../../components/cares/profile/my-profile-page";
import FaqPage from "../../../components/cares/faq/faq-page";
import { adminSections } from "../../../components/cares/admin/config";
import { adminSettingsSections } from "../../../components/cares/admin-settings/config";
import { myScheduleSections } from "../../../components/cares/my-schedule/config";
import DraftReviewPage from "../../../components/cares/scheduler-review/draft-review-page";
import RequestQueuePage from "../../../components/cares/scheduler-review/request-queue-page";
import { schedulerReviewSections } from "../../../components/cares/scheduler-review/config";
import ScheduleSettingsPage from "../../../components/cares/schedule-settings/schedule-settings-page";
import { scheduleSettingsSections } from "../../../components/cares/schedule-settings/config";
import type { RouteDefinition } from "./route-types";

const PreserveQueryRedirect = ({ to }: { to: string }) => {
    const location = useLocation();

    return (
        <Navigate
            to={`${to}${location.search}${location.hash}`}
            replace
        />
    );
};

const legacyAliasRoute = (id: number, canonicalPath: string): RouteDefinition => ({
    id,
    path: `/app${canonicalPath}`,
    element: <PreserveQueryRedirect to={canonicalPath} />,
});

const ClientWorkspaceLegacyRedirect = () => {
    const location = useLocation();

    return (
        <Navigate
            to={`${location.pathname.replace('/app/clients/', '/clients/')}${location.search}${location.hash}`}
            replace
        />
    );
};

const StructureRosterLegacyRedirect = () => {
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const requestedDomain = params.get('domain');
    const domain = ['locations', 'services', 'providers', 'roles-shifts'].includes(requestedDomain ?? '')
        ? requestedDomain
        : 'locations';
    params.delete('domain');
    const query = params.toString();

    return <Navigate to={`/scheduling/settings/${domain}${query ? `?${query}` : ''}${location.hash}`} replace />;
};

const PersonalDisplayRedirect = () => {
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    params.set('tab', 'display');

    return <Navigate to={`/user-settings?${params.toString()}${location.hash}`} replace />;
};

export const caresRouteData: RouteDefinition[] = [
    ...adminSections.map((section, index) => ({
        id: 900 + index,
        path: section.path,
        element: <AdminPage sectionId={section.id} />,
    })),
    ...adminSections.map((section, index) => ({
        id: 940 + index,
        path: section.path.replace('/admin', '/app/admin'),
        element: <PreserveQueryRedirect to={section.path} />,
    })),
    { id: 979, path: '/admin/companies', element: <PreserveQueryRedirect to="/admin/clients" /> },
    { id: 9792, path: '/app/admin/companies', element: <PreserveQueryRedirect to="/admin/clients" /> },
    { id: 976, path: '/user-settings', element: <MyProfilePage /> },
    { id: 9761, path: '/app/user-settings', element: <PreserveQueryRedirect to="/user-settings" /> },
    { id: 9762, path: '/my-schedule/end-user-settings', element: <PreserveQueryRedirect to="/user-settings" /> },
    { id: 9763, path: '/app/my-schedule/end-user-settings', element: <PreserveQueryRedirect to="/user-settings" /> },
    { id: 977, path: '/my-schedule/my-profile', element: <PreserveQueryRedirect to="/user-settings" /> },
    { id: 978, path: '/user-profile', element: <PreserveQueryRedirect to="/user-settings" /> },
    { id: 9790, path: '/my-schedule/requests/policy', element: <RequestPolicyPage /> },
    { id: 9791, path: '/app/my-schedule/requests/policy', element: <PreserveQueryRedirect to="/my-schedule/requests/policy" /> },
    { id: 980, path: '/clients/:companySlug/overview', element: <ClientWorkspacePage sectionId="overview" /> },
    { id: 981, path: '/clients/:companySlug/info', element: <ClientWorkspacePage sectionId="info" /> },
    { id: 982, path: '/clients/:companySlug/users', element: <ClientWorkspacePage sectionId="users" /> },
    { id: 983, path: '/clients/:companySlug/roles', element: <ClientWorkspacePage sectionId="roles" /> },
    { id: 984, path: '/app/clients/:companySlug/overview', element: <ClientWorkspaceLegacyRedirect /> },
    { id: 985, path: '/app/clients/:companySlug/info', element: <ClientWorkspaceLegacyRedirect /> },
    { id: 986, path: '/app/clients/:companySlug/users', element: <ClientWorkspaceLegacyRedirect /> },
    { id: 987, path: '/app/clients/:companySlug/roles', element: <ClientWorkspaceLegacyRedirect /> },
    ...myScheduleSections.map((section, index) => ({
        id: 990 + index,
        path: section.path,
        element:
            section.id === 'overview'
                ? <MyScheduleHomePage />
                : section.id === 'my-schedule'
                    ? <CalendarPage />
                    : section.id === 'my-requests'
                        ? <RequestsPage />
                        : section.id === 'swap-shifts'
                            ? <SwapShiftsPage />
                        : section.id === 'group-schedule'
                            ? <GroupSchedulePage />
                            : section.id === 'group-tallies'
                                ? <GroupTalliesPage />
                                : <MyProfilePage />,
    })),
    ...myScheduleSections
        .filter((section) => section.path.startsWith('/my-schedule/'))
        .map((section, index) => legacyAliasRoute(1200 + index, section.path)),
    ...schedulerReviewSections.map((section, index) => ({
        id: 995 + index,
        path: section.path,
        element: section.id === 'draft-review' ? <DraftReviewPage /> : <RequestQueuePage />,
    })),
    { id: 1021, path: '/scheduling/group-schedule', element: <GroupSchedulePage /> },
    { id: 1022, path: '/scheduling/group-tallies', element: <GroupTalliesPage /> },
    { id: 1023, path: '/app/scheduling/group-schedule', element: <PreserveQueryRedirect to="/scheduling/group-schedule" /> },
    { id: 1024, path: '/app/scheduling/group-tallies', element: <PreserveQueryRedirect to="/scheduling/group-tallies" /> },
    ...schedulerReviewSections.map((section, index) => legacyAliasRoute(998 + index, section.path)),
    ...schedulerReviewSections.map((section, index) => ({
        id: 1015 + index,
        path: `/scheduler-review/${section.id}`,
        element: <PreserveQueryRedirect to={section.path} />,
    })),
    ...schedulerReviewSections.map((section, index) => ({
        id: 1018 + index,
        path: `/app/scheduler-review/${section.id}`,
        element: <PreserveQueryRedirect to={section.path} />,
    })),
    ...scheduleSettingsSections.map((section, index) => ({
        id: 1000 + index,
        path: section.path,
        element: <ScheduleSettingsPage sectionId={section.id} />,
    })),
    { id: 1178, path: '/scheduling/settings/structure-roster', element: <StructureRosterLegacyRedirect /> },
    { id: 11781, path: '/app/scheduling/settings/structure-roster', element: <StructureRosterLegacyRedirect /> },
    { id: 11782, path: '/schedule-settings/structure-roster', element: <StructureRosterLegacyRedirect /> },
    { id: 11783, path: '/app/schedule-settings/structure-roster', element: <StructureRosterLegacyRedirect /> },
    { id: 1179, path: '/scheduling/settings/display-preferences', element: <PersonalDisplayRedirect /> },
    { id: 11791, path: '/app/scheduling/settings/display-preferences', element: <PersonalDisplayRedirect /> },
    ...['coverage-templates', 'validation'].map((legacySection, index) => ({
        id: 1184 + index,
        path: `/scheduling/settings/${legacySection}`,
        element: <PreserveQueryRedirect to="/scheduling/settings/imports" />,
    })),
    ...['coverage-templates', 'validation'].flatMap((legacySection, index) => [
        { id: 1210 + index, path: `/schedule-settings/${legacySection}`, element: <PreserveQueryRedirect to="/scheduling/settings/imports" /> },
        { id: 1220 + index, path: `/app/schedule-settings/${legacySection}`, element: <PreserveQueryRedirect to="/scheduling/settings/imports" /> },
    ]),
    ...scheduleSettingsSections.map((section, index) => legacyAliasRoute(1100 + index, section.path)),
    ...scheduleSettingsSections.map((section, index) => ({
        id: 1120 + index,
        path: section.id === 'overview' ? '/schedule-settings/overview' : `/schedule-settings/${section.id}`,
        element: <PreserveQueryRedirect to={section.path} />,
    })),
    ...scheduleSettingsSections.map((section, index) => ({
        id: 1140 + index,
        path: section.id === 'overview' ? '/app/schedule-settings/overview' : `/app/schedule-settings/${section.id}`,
        element: <PreserveQueryRedirect to={section.path} />,
    })),
    { id: 1160, path: '/scheduling', element: <PreserveQueryRedirect to="/scheduling/overview" /> },
    { id: 1161, path: '/app/scheduling', element: <PreserveQueryRedirect to="/scheduling/overview" /> },
    { id: 1162, path: '/scheduler-review', element: <PreserveQueryRedirect to="/scheduling/draft-review" /> },
    { id: 1163, path: '/schedule-settings', element: <PreserveQueryRedirect to="/scheduling/overview" /> },
    { id: 1164, path: '/app/scheduler-review', element: <PreserveQueryRedirect to="/scheduling/draft-review" /> },
    { id: 1165, path: '/app/schedule-settings', element: <PreserveQueryRedirect to="/scheduling/overview" /> },
    ...adminSettingsSections.map((section, index) => ({
        id: 1300 + index,
        path: section.path,
        element: <AdminSettingsPage sectionId={section.id} />,
    })),
    ...adminSettingsSections.map((section, index) => legacyAliasRoute(1350 + index, section.path)),
    { id: 1392, path: '/admin-settings/service-lines', element: <PreserveQueryRedirect to="/admin-settings/groups" /> },
    { id: 1393, path: '/app/admin-settings/service-lines', element: <PreserveQueryRedirect to="/admin-settings/groups" /> },
    { id: 1394, path: '/admin-settings/schedule-types', element: <PreserveQueryRedirect to="/admin-settings/groups" /> },
    { id: 1395, path: '/app/admin-settings/schedule-types', element: <PreserveQueryRedirect to="/admin-settings/groups" /> },
    { id: 1396, path: '/admin-settings/groups-teams', element: <PreserveQueryRedirect to="/admin-settings/groups" /> },
    { id: 1397, path: '/app/admin-settings/groups-teams', element: <PreserveQueryRedirect to="/admin-settings/groups" /> },
    { id: 13971, path: '/admin-settings/readiness-views', element: <PreserveQueryRedirect to="/admin-settings/overview" /> },
    { id: 13972, path: '/app/admin-settings/readiness-views', element: <PreserveQueryRedirect to="/admin-settings/overview" /> },
    { id: 1398, path: '/admin-settings', element: <PreserveQueryRedirect to="/admin-settings/overview" /> },
    { id: 1399, path: '/app/admin-settings', element: <PreserveQueryRedirect to="/admin-settings/overview" /> },
    { id: 1400, path: '/faq', element: <FaqPage /> },
    { id: 1401, path: '/faq/readmes', element: <PreserveQueryRedirect to="/faq" /> },
    { id: 1402, path: '/app/faq', element: <PreserveQueryRedirect to="/faq" /> },
    { id: 1403, path: '/app/faq/readmes', element: <PreserveQueryRedirect to="/faq" /> },
];
